import i18n from '@/plugins/vue-i18n';
import router from '@/router';
import { cloneDeep } from 'lodash';
import { defineStore } from 'pinia';

export const useStoreNotify = defineStore('notify', {
  state: () => ({
    notifications: [],
  }),

  getters: {
    hasAnyUniqueNotification(state) {
      if (state.notifications.length === 0) return false;
      return state.notifications.some((notification) => notification.isUnique);
    },
  },

  actions: {
    /**
     * Notify a message with the given parameters.
     * @param {string} id - Unique identifier for the notification. If not provided, a unique ID will be generated.
     * @param {string} message - The main message to display in the notification.
     * @param {string} details - Additional details to display in the notification.
     * @param {string} type - The type of notification (e.g., 'info', 'success', 'warning', 'error'). If not provided, it defaults to 'info'.
     * @param {boolean} closable - Whether the notification can be closed by the user. Defaults to true.
     * @param {number} timeout - The duration in milliseconds before the notification automatically closes. Defaults to 5000ms.
     * @param {boolean} loading - Whether the notification is in a loading state. Defaults to false.
     * @param {string} linkText - Text for a clickable link in the notification. If provided, the notification will include a link.
     * @param {function} onClickLink - Callback function to execute when the link is clicked.
     * @param {boolean} isUnique - Whether the notification should be unique (i.e., not allow duplicates with the same message). Defaults to false.
     * @returns {void}
     */
    notify({
      id,
      message,
      details,
      type,
      closable = true,
      timeout = 5000,
      loading,
      linkText,
      onClickLink,
      isUnique = false,
    }) {
      const notification = {
        id: id ?? 'ag-notification-' + Date.now(),
        message,
        details,
        type,
        closable,
        timeout,
        loading,
        linkText,
        onClickLink,
        isUnique,
      };

      if (notification.isUnique && this.hasNotificationWithMessage(notification.message)) return;

      this.notifications = [...cloneDeep(this.notifications), notification];
    },

    notifyInfo({ id, message, details, closable, timeout, loading, linkText, onClickLink, isUnique }) {
      this.notify({ id, message, details, type: 'info', closable, timeout, loading, linkText, onClickLink, isUnique });
    },

    notifySuccess({ id, message, details, closable, timeout, loading, linkText, onClickLink, isUnique }) {
      this.notify({
        id,
        message,
        details,
        type: 'success',
        closable,
        timeout,
        loading,
        linkText,
        onClickLink,
        isUnique,
      });
    },

    notifyWarning({ id, message, details, closable, timeout, loading, linkText, onClickLink, isUnique }) {
      this.notify({
        id,
        message,
        details,
        type: 'warning',
        closable,
        timeout,
        loading,
        linkText,
        onClickLink,
        isUnique,
      });
    },

    notifyError({ id, message, details, closable, timeout, loading, linkText, onClickLink, isUnique }) {
      this.notify({ id, message, details, type: 'error', closable, timeout, loading, linkText, onClickLink, isUnique });
    },

    notifyExportSuccess() {
      this.notifySuccess({
        message: i18n.global.t('toast.exportSuccess'),
        linkText: 'Clique aqui para acessar',
        closable: true,
        timeout: 5000,
        onClickLink: () => router.push({ name: 'RouteDocumentsDownload' }),
      });
    },

    notifyExportError() {
      this.notifyError({
        message: i18n.global.t('toast.exportError'),
        closable: true,
        timeout: 5000,
      });
    },

    notifyDownloadSuccess() {
      this.notifySuccess({
        message: i18n.global.t('toast.downloadSuccess'),
        closable: true,
        timeout: 5000,
      });
    },

    notifyDownloadError() {
      this.notifyError({
        message: i18n.global.t('toast.downloadError'),
        closable: true,
        timeout: 5000,
      });
    },

    removeNotification(notificationId) {
      this.notifications = cloneDeep(this.notifications.filter((notification) => notification.id !== notificationId));
    },

    hasNotificationWithMessage(message) {
      return this.notifications.some((notification) => notification.message === message);
    },
  },
});
